import React from 'react'
import {createUseStyles} from 'react-jss'
import {Colors} from "../../../constants/colors";

type Props = {
  /** Score between 0 and 1 (null/undefined allowed) */
  score: number | null | undefined
  /** Optional: show as percentage instead of 0.xx */
  format?: 'decimal' | 'percent'
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

const getColorClass = (score: number) => {
  if (score < 0.2) return 'good'
  if (score < 0.6) return 'warn'
  return 'bad'
}

const ScoreBar = ({score, format = 'decimal'}: Props) => {
  const styles = useStyles()

  if (score == null || Number.isNaN(score)) {
    return <span className={styles.na}>-</span>
  }

  const s = clamp01(score)
  const widthPct = `${Math.round(s * 100)}%`
  const colorClass = getColorClass(s)

  const label =
    format === 'percent'
      ? `${(s * 100).toFixed(0)}%`
      : s.toFixed(2)

  return (
    <div className={styles.wrap} title={`Score: ${label}`}>
      <div className={styles.track}>
        <div className={`${styles.fill} ${styles[colorClass]}`} style={{width: widthPct}} />
        <div className={styles.label}>{label}</div>
      </div>
    </div>
  )
}

export default ScoreBar

const useStyles = createUseStyles({
  wrap: {
    width: 92,
  },
  track: {
    position: 'relative',
    height: 16,
    borderRadius: 6,
    background: Colors.grayCard,
    overflow: 'hidden',
  },
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: 6,
    transition: 'width 120ms linear',
  },
  good: {
    background: Colors.euro_500
  },
  warn: {
    background: Colors.yen_800
  },
  bad: {
    background: Colors.rubel_500
  },
  label: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Aestetico-Regular',
    color: Colors.gray0,
    textShadow: [
      '0 0 1px rgba(0,0,0,0.95)',
      '0 1px 0 rgba(0,0,0,0.95)',
      '0 2px 2px rgba(0,0,0,0.70)',
    ].join(', '),
    pointerEvents: 'none',
  },
  na: {
    opacity: 0.75,
  },
})
